import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { Mail, Phone, ArrowRight, Loader2, CheckCircle, Shield } from 'lucide-react';

const API = '/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();

  const [tab, setTab] = useState('google');

  // Email OTP state
  const [email, setEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailStep, setEmailStep] = useState('email'); // email | otp

  // Mobile OTP state
  const [mobile, setMobile] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [mobileStep, setMobileStep] = useState('mobile'); // mobile | otp
  const [mobileMasked, setMobileMasked] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  // After updateUser(), useEffect commits the navigation once user is in context
  const [pendingRoute, setPendingRoute] = useState(null);
  useEffect(() => {
    if (user && pendingRoute) {
      navigate(pendingRoute, { replace: true });
      setPendingRoute(null);
    }
  }, [user, pendingRoute, navigate]);

  if (user && !pendingRoute) {
    return <Navigate to={user.role === 'admin' ? '/admin' : from} replace />;
  }

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // ── Email OTP handlers ──────────────────────────────────────────
  const handleSendEmailOtp = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Please enter your email');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/email-otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setEmailStep('otp');
      toast.success('OTP sent! Check your inbox.');
    } catch (err) {
      toast.error(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    if (!emailOtp) return toast.error('Please enter the OTP');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/email-otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, otp: emailOtp })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      updateUser(data.user);
      toast.success(`Welcome, ${data.user.name}!`);
      setPendingRoute(data.user.is_new_user ? '/role-select' : (data.user.role === 'admin' ? '/admin' : from));
    } catch (err) {
      toast.error(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // ── Mobile OTP handlers ─────────────────────────────────────────
  const handleSendMobileOtp = async (e, isResend = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (mobile.length !== 10) return toast.error('Enter a valid 10-digit mobile number');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mobile-otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setMobileMasked(data.message);
      setMobileStep('otp');
      setMobileOtp('');
      // Start 30s resend cooldown
      setResendCooldown(30);
      const timer = setInterval(() => {
        setResendCooldown(prev => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
      }, 1000);
      toast.success(isResend ? 'New OTP sent!' : 'OTP sent to your mobile!');
    } catch (err) {
      toast.error(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMobileOtp = async (e) => {
    e.preventDefault();
    if (mobileOtp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mobile-otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ mobile, otp: mobileOtp })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Invalid OTP. Please try again.');
      }
      const data = await res.json();
      updateUser(data.user);
      toast.success(`Welcome to Rent Mitra!`);
      setPendingRoute(data.user.is_new_user ? '/role-select' : (data.user.role === 'admin' ? '/admin' : from));
    } catch (err) {
      toast.error(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Login | Rent Mitra</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src="/logo.png" alt="Rent Mitra" className="h-16 w-auto object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{fontFamily:'Outfit,sans-serif'}}>Welcome Back</h1>
            <p className="text-slate-500 text-sm">Sign in or register to get started</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-[0_8px_40px_rgb(0,0,0,0.08)] border border-slate-100 p-8">
            {/* Tabs */}
            <div className="flex rounded-xl bg-slate-100 p-1 mb-7 gap-0.5">
              <button
                onClick={() => setTab('google')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${tab === 'google' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="tab-google"
              >
                Google
              </button>
              <button
                onClick={() => setTab('email')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${tab === 'email' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="tab-email"
              >
                Email OTP
              </button>
              <button
                onClick={() => setTab('mobile')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${tab === 'mobile' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="tab-mobile"
              >
                Mobile OTP
              </button>
            </div>

            {/* ── Google Tab ── */}
            {tab === 'google' && (
              <div className="space-y-4">
                <button
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-800 py-3.5 rounded-xl font-semibold text-sm transition-all"
                  data-testid="google-login-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
                <p className="text-center text-xs text-slate-400">Fast, secure login via Google account</p>
              </div>
            )}

            {/* ── Email OTP Tab ── */}
            {tab === 'email' && (
              <div>
                {emailStep === 'email' ? (
                  <form onSubmit={handleSendEmailOtp} className="space-y-4" data-testid="email-otp-form">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                          data-testid="email-input"
                          required
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                      data-testid="send-otp-button"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      {loading ? 'Sending...' : 'Send OTP'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyEmailOtp} className="space-y-4" data-testid="verify-otp-form">
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-2 flex gap-3 items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-blue-900">OTP sent to your email</p>
                        <p className="text-xs text-blue-600 mt-0.5">{email}</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Enter 6-digit OTP</label>
                      <input
                        type="text"
                        value={emailOtp}
                        onChange={e => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="• • • • • •"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-center text-2xl font-bold tracking-[0.5em]"
                        data-testid="otp-input"
                        maxLength={6}
                        required
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading || emailOtp.length < 6}
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                      data-testid="verify-otp-button"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {loading ? 'Verifying...' : 'Verify & Sign In'}
                    </button>
                    <button type="button" onClick={() => { setEmailStep('email'); setEmailOtp(''); }} className="w-full text-slate-500 text-sm hover:text-slate-700 transition-colors">
                      Use a different email
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* ── Mobile OTP Tab ── */}
            {tab === 'mobile' && (
              <div>
                {mobileStep === 'mobile' ? (
                  <form onSubmit={handleSendMobileOtp} className="space-y-4" data-testid="mobile-login-form">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile Number</label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 flex-shrink-0">
                          <span className="text-base">🇮🇳</span> +91
                        </div>
                        <input
                          type="tel"
                          value={mobile}
                          onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="9876543210"
                          className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 font-semibold tracking-widest"
                          data-testid="mobile-login-input"
                          required autoFocus
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-2">We'll send a 6-digit OTP to verify your number</p>
                    </div>
                    <button
                      type="submit"
                      disabled={loading || mobile.length !== 10}
                      className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                      data-testid="send-mobile-otp-login-button"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                      {loading ? 'Sending OTP...' : 'Send OTP via SMS'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyMobileOtp} className="space-y-4" data-testid="mobile-otp-login-form">
                    <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex gap-3 items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-green-900">{mobileMasked}</p>
                        <p className="text-xs text-green-600 mt-0.5">Enter the 6-digit code below</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Enter Code</label>
                      <input
                        type="text"
                        value={mobileOtp}
                        onChange={e => setMobileOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="• • • • • •"
                        className="w-full px-4 py-4 border border-slate-200 rounded-xl text-2xl font-bold tracking-[0.5em] text-center outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        data-testid="mobile-otp-login-input"
                        maxLength={6} autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading || mobileOtp.length < 6}
                      className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                      data-testid="verify-mobile-otp-login-button"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                      {loading ? 'Verifying...' : 'Verify & Sign In'}
                    </button>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => { setMobileStep('mobile'); setMobileOtp(''); setResendCooldown(0); }}
                        className="text-slate-500 text-sm hover:text-slate-700 transition-colors"
                      >
                        Change number
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleSendMobileOtp(e, true)}
                        disabled={loading || resendCooldown > 0}
                        className="text-blue-600 text-sm hover:text-blue-800 transition-colors disabled:opacity-40 font-medium"
                        data-testid="resend-otp-button"
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            By signing in, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}
